import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import Button from '../../components/ui/Button'
import PageHeader from '../../components/ui/PageHeader'
import { useToast } from '../../components/ui/ToastProvider'
import { businessToday, toISODate } from '../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import AgendaFormDialog from './components/AgendaFormDialog'
import WeekGrid from './components/WeekGrid'
import WeekToolbar from './components/WeekToolbar'
import { useAgendaCalendar } from './hooks/useAgendas'
import { useProxyMode } from '../proxy/useProxyMode'
import {
  MONTH_KEYS,
  WEEK_LENGTH,
  WEEK_PARAM,
  addDays,
  resolveWeekStart,
  suggestedDay,
  weekDays,
  weekTitle,
} from './week'
import './agenda.css'
import '../admin/admin.css'

/**
 * Görüntülenen hafta URL'dedir (`?week=`) — detaydan dönünce aynı hafta açılır. Bu haftada parametre
 * silinir; gezinme geçmişi şişirmesin diye `replace`.
 */
function useWeekParam() {
  const [params, setParams] = useSearchParams()
  const monday = resolveWeekStart(params.get(WEEK_PARAM))
  const goTo = (next: Date | null) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev)
        if (next) out.set(WEEK_PARAM, toISODate(next))
        else out.delete(WEEK_PARAM)
        return out
      },
      { replace: true },
    )
  return { monday, goTo }
}

/**
 * Ajanda ana ekranı: haftalık takvim (Pazartesi–Pazar). Hangi toplantıların geleceğine sunucu karar
 * verir; ekran yalnız günlere yerleştirir. Boş hafta ızgarayı silmez, üstünde kısa not çıkar.
 */
export default function AgendaPage() {
  const { t } = useTranslation()
  const { readOnly } = useProxyMode()
  const toast = useToast()
  const location = useLocation()
  const stacked = useMediaQuery(CARD_VIEW_BREAKPOINT)
  const { monday, goTo } = useWeekParam()
  const today = businessToday()

  const from = toISODate(monday)
  const calendar = useAgendaCalendar(from, toISODate(addDays(monday, WEEK_LENGTH)))
  const items = calendar.data ?? []

  // Açık formun önerdiği gün; `null` → form kapalı.
  const [formDay, setFormDay] = useState<string | null>(null)

  const onSaved = () => {
    toast.success(t('agenda.created'))
    setFormDay(null)
  }

  return (
    <div className="admin-page agenda-page">
      <PageHeader
        // Başlık = menü adı: menüyle aynı anahtar.
        title={t('nav.tutanak.agenda')}
        description={t('agenda.description')}
        actions={
          /* Kısıtlı Yetkili asistan kipinde YAZMA EYLEMİ SUNULMAZ (126 FR-019). Düğmeyi çizip
             sunucuya reddettirmek, tasarımın kaçınmak için kurulduğu çıkmazın ta kendisi olurdu:
             "düğmeyi görüyorum ama basınca hata alıyorum". Takvimin KENDİSİ görünür kalır —
             asistanın daveti görmesi isteğin tanımında var (FR-020). */
          readOnly ? null : (
            <Button
              icon="pi pi-plus"
              label={t('agenda.new')}
              onClick={() => setFormDay(toISODate(suggestedDay(monday, today)))}
            />
          )
        }
      />

      <WeekToolbar
        title={weekTitle(monday, (m) => t(`agenda.week.months.${MONTH_KEYS[m]}`))}
        onToday={() => goTo(null)}
        onPrevious={() => goTo(addDays(monday, -WEEK_LENGTH))}
        onNext={() => goTo(addDays(monday, WEEK_LENGTH))}
        onRefresh={() => calendar.refetch()}
      />

      {calendar.isSuccess && items.length === 0 && <EmptyWeekHint readOnly={readOnly} />}

      <WeekGrid
        days={weekDays(monday)}
        items={items}
        today={today}
        loading={calendar.isLoading}
        stacked={stacked}
        from={`${location.pathname}${location.search}`}
        onCreate={setFormDay}
        canCreateAny={!readOnly}
      />

      {/* Form da kipe bağlı: "+" düğmesini gizlemek yetmez — `formDay` başka bir yoldan dolarsa
          (ileride eklenecek bir kısayol, derin bağlantı) form yine açılırdı. Detay sayfasındaki
          aynı dialog `canWrite` ile sarılı; burada sarılı DEĞİLDİ (push kapısı review'ü). */}
      {!readOnly && (
        <AgendaFormDialog
          visible={formDay !== null}
          initial={null}
          defaultDate={formDay ?? undefined}
          onHide={() => setFormDay(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  )
}

/**
 * Boş hafta notu (ızgara yerinde kalır, gezinme ve "+" çalışsın).
 *
 * <p>Açıklama satırı <b>Kısıtlı Yetkili asistanda düşer</b> (126, push kapısı review'ü 21.10.2026):
 * metin bir EYLEM ÇAĞRISIDIR (*"bir günün + düğmesiyle ya da «Yeni Davet» ile…"*) ve o kipte
 * saydığı iki yüzey de çizilmiyor. Başlık bilgiyi taşıyor; açıklamayı bırakmak, olmayan bir düğmeyi
 * tarif ederek FR-019'un kapatmaya çalıştığı yanılgıyı üretirdi.
 */
function EmptyWeekHint({ readOnly }: Readonly<{ readOnly: boolean }>) {
  const { t } = useTranslation()
  return (
    <p className="agenda-week-empty" role="status">
      <i className="pi pi-calendar" aria-hidden="true" />
      <span className="agenda-week-empty-title">{t('agenda.week.emptyTitle')}</span>
      {!readOnly && <span className="agenda-muted">{t('agenda.week.emptyDesc')}</span>}
    </p>
  )
}
